// CleanupIvansCreatePolicyOverlay — "the carrier sent us a policy we
// don't have; put it in the OS" in one place.
//
// Grant 2026-08-18: "i dont want her to click around all over. i want it
// to be easy to just click and create a policy for that IVANS data if
// need be." Before this, the cleanup item listed everything the carrier
// sent — insured, address, coverage, term, premium — and then left Joy
// or Mylah to go find the household, open it, click Add policy, and
// re-type all of it.
//
// Two paths, matching the backend's two branches (the `create-policy`
// verb in workers/api/src/routes/cleanup.ts):
//   • EXISTING client — search → pick → create. The policy lands on that
//     household with the carrier's own numbers.
//   • NEW client — the carrier already gave us the insured's name, so
//     the form is pre-filled; confirm and the household, the person and
//     the policy are all created together. If someone with that name is
//     already in the OS the server REFUSES (409) rather than minting a
//     second copy — the panel then says so and points back at search.
//
// WHAT THIS FORM DOES NOT SEND: any policy data. Carrier, policy number,
// coverage, effective/expiration dates and premium are re-read
// server-side from the carrier's own transaction. The browser supplies
// only the destination (and, for a new client, their name) — so nothing
// a user could mistype here can end up on the record.
//
// The backend marks the issue resolved on success, so onCreated() just
// refreshes the host's queue.

import { useEffect, useState } from "react";

import { api, ApiError, type HouseholdListItem } from "../api";
import { Modal } from "../components/Modal";
import { Button } from "../ui";
import { CANCELLATION_REASON_OPTIONS } from "../components/service-records/insurance-cancellation-reasons";
import { DateField } from "../components/DateField";

type Mode = "existing" | "create";

/** Everything this panel needs from a household — a search result and
 *  the matcher's own suggestion both satisfy it. */
type PickedHousehold = { id: string; display_name: string };

// The OS policy types, in the order a P&C book actually uses them. Shown
// only when the carrier's line-of-business code has no unambiguous
// equivalent (the server refuses to guess one — a wrong type drives the
// record's service_type and label).
const POLICY_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "home", label: "Home" },
  { value: "auto", label: "Auto" },
  { value: "umbrella", label: "Umbrella" },
  { value: "condo", label: "Condo" },
  { value: "renters", label: "Renters" },
  { value: "dwelling_fire", label: "Dwelling / Fire" },
  { value: "secondary_home", label: "Secondary home" },
  { value: "watercraft", label: "Watercraft" },
  { value: "motorcycle", label: "ATV / Motorcycle" },
  { value: "rv", label: "RV / Travel Trailer" },
  { value: "flood", label: "Flood" },
  { value: "golf_cart", label: "Golf cart" },
  { value: "commercial", label: "Commercial" },
  { value: "other", label: "Other" },
];

export function CleanupIvansCreatePolicyOverlay({
  issueId,
  carrierLabel,
  decisionQuestion,
  suggestedFirstName,
  suggestedLastName,
  suggestedPolicyType,
  suggestedHousehold = null,
  onClose,
  onCreated,
}: {
  issueId: string;
  /** The affected-record label — "Progressive · Auto (personal) · #123 · Jim Moore". */
  carrierLabel: string;
  decisionQuestion: string;
  suggestedFirstName: string;
  suggestedLastName: string;
  /** "" when the carrier's LOB code has no OS equivalent. */
  suggestedPolicyType: string;
  /** The household the matcher already identified by address (and
   *  usually surname), or null when nobody matched. Pre-selected so the
   *  common case is confirm-and-go; the search stays right there to
   *  change it, and the server re-reads whichever id is submitted. */
  suggestedHousehold?: { id: string; name: string } | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<Mode>("existing");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The server refused to mint a second copy of someone already here.
  const [duplicateBlock, setDuplicateBlock] = useState<string | null>(null);

  // --- existing-household search state ---
  // Seeded with the carrier's surname ONLY when nothing is pre-selected.
  // With a suggestion on screen, opening straight into a result list
  // would bury the one row that is probably right.
  const [query, setQuery] = useState(suggestedHousehold ? "" : suggestedLastName || "");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Only id + name are ever used, so a search result and the matcher's
  // suggestion are interchangeable here.
  const [picked, setPicked] = useState<PickedHousehold | null>(
    suggestedHousehold ? { id: suggestedHousehold.id, display_name: suggestedHousehold.name } : null,
  );
  const suggestionStillPicked = Boolean(
    suggestedHousehold && picked && picked.id === suggestedHousehold.id,
  );

  // --- new-client form state, pre-filled from the carrier's insured ---
  const [firstName, setFirstName] = useState(suggestedFirstName);
  const [lastName, setLastName] = useState(suggestedLastName);

  // --- policy type: only asked when the carrier's code didn't map ---
  const [policyType, setPolicyType] = useState(suggestedPolicyType);
  const needsPolicyType = suggestedPolicyType.length === 0;

  // --- already ended (Mylah, 2026-09-03) ---
  // The carrier's latest message can still say a policy is in force (a
  // "policy sync" restating what the carrier currently believes it
  // holds) while the steward already knows it isn't — from the client,
  // from a later download, from their own records. This records the
  // policy AND its end in one step instead of creating it active and
  // cancelling it right after, so the household is never briefly wrong
  // on screen and never briefly re-enters the renewal queue.
  const [alreadyEnded, setAlreadyEnded] = useState(false);
  const [endedReason, setEndedReason] = useState("");
  const [endedAt, setEndedAt] = useState("");
  const endedReady = !alreadyEnded || (endedReason.length > 0 && endedAt.length > 0);

  function alreadyEndedParams(): Record<string, unknown> {
    return alreadyEnded
      ? {
          already_ended: true,
          cancellation_reason: endedReason,
          cancelled_at: endedAt,
        }
      : {};
  }

  // Debounced search (mirrors CleanupRealtyLinkOverlay: 250ms, 2-char
  // floor). Seeded with the carrier's surname so the likely household is
  // usually already on screen when the panel opens.
  useEffect(() => {
    if (mode !== "existing" || query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({ q: query.trim(), limit: 20 });
        if (cancelled) return;
        setResults(res.households);
        setSearchError(null);
      } catch (err) {
        if (cancelled) return;
        setSearchError(err instanceof ApiError ? err.message : "Search failed");
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, mode]);

  function policyTypeParam(): Record<string, unknown> {
    return needsPolicyType && policyType ? { policy_type: policyType } : {};
  }

  function describeFailure(err: unknown): string {
    if (err instanceof ApiError) return err.message;
    return "Couldn't create the policy. Nothing was changed.";
  }

  async function createOnExisting() {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.decideCleanupIssue({
        issue_id: issueId,
        verb: "create-policy",
        overrode_recommendation: false,
        params: {
          target_household_id: picked.id,
          ...policyTypeParam(),
          ...alreadyEndedParams(),
        },
      });
      onCreated();
    } catch (err) {
      setError(describeFailure(err));
    } finally {
      setBusy(false);
    }
  }

  async function createForNewClient(allowDuplicate: boolean) {
    if (busy) return;
    if (!firstName.trim() && !lastName.trim()) {
      setError("Enter the client's name — the carrier's insured is a good start.");
      return;
    }
    setBusy(true);
    setError(null);
    if (!allowDuplicate) setDuplicateBlock(null);
    try {
      await api.decideCleanupIssue({
        issue_id: issueId,
        verb: "create-policy",
        overrode_recommendation: false,
        params: {
          create_household: true,
          ...(firstName.trim() ? { new_first_name: firstName.trim() } : {}),
          ...(lastName.trim() ? { new_last_name: lastName.trim() } : {}),
          ...(allowDuplicate ? { allow_duplicate: true } : {}),
          ...policyTypeParam(),
          ...alreadyEndedParams(),
        },
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError && err.code === "duplicate_contact") {
        setDuplicateBlock(
          err.message ||
            "Someone with this name is already in the OS — find their household instead of creating a second one.",
        );
      } else {
        setError(describeFailure(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const typeReady = !needsPolicyType || policyType.length > 0;

  return (
    <Modal
      open={true}
      title="Add the carrier's policy to the OS"
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {mode === "existing" ? (
            <Button
              onClick={createOnExisting}
              disabled={!picked || busy || !typeReady || !endedReady}
            >
              {busy
                ? "Creating…"
                : picked
                  ? alreadyEnded
                    ? `Add to "${picked.display_name}" (already ended)`
                    : `Add to "${picked.display_name}"`
                  : "Pick a household first"}
            </Button>
          ) : duplicateBlock ? (
            <Button
              onClick={() => createForNewClient(true)}
              disabled={busy || !endedReady}
            >
              {busy ? "Creating…" : "Create anyway"}
            </Button>
          ) : (
            <Button
              onClick={() => createForNewClient(false)}
              disabled={
                busy ||
                !typeReady ||
                !endedReady ||
                (!firstName.trim() && !lastName.trim())
              }
            >
              {busy ? "Creating…" : "Create client & policy"}
            </Button>
          )}
        </>
      }
    >
      <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
        <strong>{decisionQuestion}</strong>
        <br />
        <span className="muted" style={{ fontSize: 12 }}>
          Carrier sent: {carrierLabel}
        </span>
      </p>

      <p style={{ fontSize: 12.5, marginTop: 0, color: "var(--fg-3)" }}>
        You only choose <strong>where it goes</strong>. The carrier, policy
        number, coverage, dates and premium are taken straight from the
        carrier's own download — you don't retype any of it.
      </p>

      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => {
            setMode("existing");
            setError(null);
          }}
          style={modeTabStyle(mode === "existing")}
        >
          They're already a client
        </button>
        <button
          type="button"
          onClick={() => {
            setMode("create");
            setError(null);
            setDuplicateBlock(null);
          }}
          style={modeTabStyle(mode === "create")}
        >
          New client
        </button>
      </div>

      {error && (
        <p style={{ color: "var(--danger)", fontSize: 12.5, margin: "0 0 10px" }}>
          {error}
        </p>
      )}

      {needsPolicyType && (
        <div style={{ marginBottom: 14 }}>
          <label
            htmlFor="ivans-policy-type"
            style={{ display: "block", fontSize: 13, marginBottom: 4 }}
          >
            What kind of policy is this? (the carrier's code doesn't map to one
            of ours)
          </label>
          <select
            id="ivans-policy-type"
            value={policyType}
            onChange={(e) => setPolicyType(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-compact)",
              fontSize: 14,
            }}
          >
            <option value="">Choose a type…</option>
            {POLICY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div
        style={{
          border: "1px solid var(--mid-gray)",
          borderRadius: "var(--radius-compact)",
          padding: "10px 12px",
          marginBottom: 14,
          background: alreadyEnded ? "rgba(74, 152, 198, 0.06)" : "transparent",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={alreadyEnded}
            onChange={(e) => setAlreadyEnded(e.target.checked)}
          />
          This policy has already ended
        </label>
        <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>
          Use this when the carrier is still calling it active but you know it
          isn't — replaced by another carrier, the property sold, the client
          left. The policy and its end are recorded together; the household
          never shows it as active in between.
        </p>
        {alreadyEnded && (
          <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px" }}>
              <label
                htmlFor="ivans-ended-reason"
                style={{ display: "block", fontSize: 12.5, marginBottom: 4 }}
              >
                Why it ended
              </label>
              <select
                id="ivans-ended-reason"
                value={endedReason}
                onChange={(e) => setEndedReason(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--mid-gray)",
                  borderRadius: "var(--radius-compact)",
                  fontSize: 14,
                }}
              >
                <option value="">Choose a reason…</option>
                {CANCELLATION_REASON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 150px" }}>
              <label
                htmlFor="ivans-ended-at"
                style={{ display: "block", fontSize: 12.5, marginBottom: 4 }}
              >
                Date it ended
              </label>
              <DateField
                id="ivans-ended-at"
                value={endedAt}
                onChange={setEndedAt}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--mid-gray)",
                  borderRadius: "var(--radius-compact)",
                  fontSize: 14,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {mode === "existing" ? (
        <>
          {suggestedHousehold && (
            <div
              style={{
                border: `1px solid ${
                  suggestionStillPicked ? "var(--blue-dark)" : "var(--mid-gray)"
                }`,
                borderRadius: "var(--radius-compact)",
                padding: "10px 12px",
                marginBottom: 14,
                background: suggestionStillPicked
                  ? "rgba(74, 152, 198, 0.10)"
                  : "transparent",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--blue-dark)",
                  marginBottom: 4,
                }}
              >
                Best match — from the carrier's address
              </div>
              <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 14 }}>
                {suggestedHousehold.name}
              </div>
              <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
                {suggestionStillPicked
                  ? "Selected. Check it's the right family, then add the policy — or search below to pick a different one."
                  : "Not selected. Use the search below, or click to go back to this one."}
              </p>
              {!suggestionStillPicked && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setPicked({
                      id: suggestedHousehold.id,
                      display_name: suggestedHousehold.name,
                    })
                  }
                  disabled={busy}
                  style={{ marginTop: 8 }}
                >
                  Use {suggestedHousehold.name}
                </Button>
              )}
            </div>
          )}

          <label
            htmlFor="ivans-household-search"
            style={{ display: "block", fontSize: 13, marginBottom: 4 }}
          >
            {suggestedHousehold
              ? "Or find a different household"
              : "Find the household this policy belongs to"}
          </label>
          <input
            id="ivans-household-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type at least 2 characters…"
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-compact)",
              fontSize: 14,
            }}
            autoFocus={!suggestedHousehold}
          />

          {searching && (
            <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
              Searching…
            </p>
          )}
          {searchError && (
            <p style={{ color: "var(--danger)", fontSize: 12, margin: "6px 0 0" }}>
              {searchError}
            </p>
          )}
          {!searching &&
            query.trim().length >= 2 &&
            results.length === 0 &&
            !searchError && (
              <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
                No matching households — if they aren't in the OS yet, switch to{" "}
                <strong>New client</strong> above.
              </p>
            )}

          {results.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: "8px 0 0",
                maxHeight: 240,
                overflowY: "auto",
                border: "1px solid var(--mid-gray)",
                borderRadius: "var(--radius-compact)",
              }}
            >
              {results.map((h) => {
                const isPicked = picked?.id === h.id;
                return (
                  <li key={h.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(h)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        background: isPicked
                          ? "rgba(74, 152, 198, 0.12)"
                          : "transparent",
                        border: "none",
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        borderBottom: "1px solid var(--mid-gray)",
                      }}
                    >
                      <div style={{ fontWeight: 600, color: "var(--navy)" }}>
                        {h.display_name}
                      </div>
                      {h.members_label && (
                        <div className="muted" style={{ fontSize: 11.5 }}>
                          {h.members_label}
                        </div>
                      )}
                      <div className="muted" style={{ fontSize: 11 }}>
                        {h.contact_count}{" "}
                        {h.contact_count === 1 ? "contact" : "contacts"}
                        {h.primary_city &&
                          h.primary_state &&
                          ` · ${h.primary_city}, ${h.primary_state}`}
                        {h.company_slugs.length > 0 &&
                          ` · ${h.company_slugs.join(", ")}`}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {picked && (
            <p style={{ fontSize: 12.5, marginTop: 12, color: "var(--fg-3)" }}>
              {alreadyEnded ? (
                <>
                  The policy is added to <strong>{picked.display_name}</strong> with
                  the carrier's numbers, then ended right away with the reason
                  and date you gave — it's created and ended in one step, so
                  it never sits in the Renewal Plan queue or briefly counts
                  them as an active Insurance client (unless another active
                  policy already does). Undo for 60 seconds, or reopen it
                  later from the record's own page.
                </>
              ) : (
                <>
                  The policy is added to <strong>{picked.display_name}</strong> with
                  the carrier's numbers, joins the Renewal Plan, and makes them an
                  Insurance client. Undo for 60 seconds, or archive it later from
                  the record's own page.
                </>
              )}
            </p>
          )}
        </>
      ) : (
        <>
          <p style={{ fontSize: 12.5, marginTop: 0, color: "var(--fg-3)" }}>
            Use this when the insured isn't in the OS at all. The name below is
            what the carrier sent — correct it if they got it wrong. A
            household, the person, and the policy are created together.
          </p>
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="ivans-first-name"
                style={{ display: "block", fontSize: 13, marginBottom: 4 }}
              >
                First name
              </label>
              <input
                id="ivans-first-name"
                type="text"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setDuplicateBlock(null);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--mid-gray)",
                  borderRadius: "var(--radius-compact)",
                  fontSize: 14,
                }}
                autoFocus
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="ivans-last-name"
                style={{ display: "block", fontSize: 13, marginBottom: 4 }}
              >
                Last name
              </label>
              <input
                id="ivans-last-name"
                type="text"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setDuplicateBlock(null);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid var(--mid-gray)",
                  borderRadius: "var(--radius-compact)",
                  fontSize: 14,
                }}
              />
            </div>
          </div>

          {duplicateBlock && (
            <div
              style={{
                border: "1px solid var(--blue, #4e98c6)",
                background: "rgba(74, 152, 198, 0.08)",
                borderRadius: "var(--radius-compact)",
                padding: "10px 12px",
                marginBottom: 12,
                fontSize: 12.5,
                lineHeight: 1.45,
              }}
            >
              {duplicateBlock} Switch to{" "}
              <strong>They're already a client</strong> to add the policy to the
              household they're already in — or use <strong>Create anyway</strong>{" "}
              if this really is a different person with the same name.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function modeTabStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    border: active ? "1px solid var(--blue)" : "1px solid var(--mid-gray)",
    background: active ? "var(--blue-tint, rgba(74,152,198,0.1))" : "var(--bg-0)",
    color: "var(--navy)",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
  };
}
